import React, { useState, useEffect } from 'react';

interface AppliedJob {
  id: number;
  job_listing_id: number | null;
  job_source_id: number;
  applied_at: string;
  job_listing?: {
    id: number;
    title: string;
    max_reward: number | null;
    min_reward: number | null;
    contract_type: string;
    prefecture: string | null;
    city: string | null;
    station: string | null;
    workstyle_type: number;
    workstyle_type_label: string | null;
    skills: Array<{ id: number; name: string }>;
    occupations: Array<{ id: number; name: string }>;
    job_source: {
      id: number;
      name: string;
      display_name?: string;
    };
  };
  job_source?: {
    id: number;
    name: string;
    display_name?: string;
  };
}

interface AppliedJobListingsPageProps {
  initialAppliedJobs?: AppliedJob[];
  isUserSignedIn?: boolean;
}

const AppliedJobListingsPage: React.FC<AppliedJobListingsPageProps> = ({
  initialAppliedJobs = [],
  isUserSignedIn = false
}) => {
  const [appliedJobs, setAppliedJobs] = useState<AppliedJob[]>(initialAppliedJobs);
  const [isLoading, setIsLoading] = useState(false);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    if (isUserSignedIn) {
      fetchAppliedJobs();
    }
  }, [isUserSignedIn]);

  // 応募済み案件一覧を取得
  const fetchAppliedJobs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/mypage/applied_job_listings.json', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAppliedJobs(data.applied_jobs || []);
      } else {
        console.error('Failed to fetch applied jobs:', response.status);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('応募一覧の取得に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      console.error('Error fetching applied jobs:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('応募一覧の取得に失敗しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 日付をフォーマット
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ログインしていない場合の表示
  if (!isUserSignedIn) {
    return (
      <div className="container p-4">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="h3 fw-bold mb-4">応募一覧</h1>
          <p className="text-muted mb-4">
            求人に応募すると応募一覧が見れます。<br />
            早速求人を探して応募してみましょう。
          </p>
          <a href="/job_listings/search" className="btn btn-primary">
            <i className="bi bi-search me-2"></i>求人を探す
          </a>
        </div>
      </div>
    );
  }

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="container p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
        </div>
      </div>
    );
  }

  // 応募済み案件が空の場合
  if (appliedJobs.length === 0) {
    return (
      <div className="container p-4">
        <div className="text-center py-5">
          <h1 className="h3 fw-bold mb-3">応募済み案件一覧</h1>
          <p className="text-muted mb-4">
            応募済みの求人はありません。<br />
            求人に応募すると、ここに表示されます。
          </p>
          <a href="/job_listings/search" className="btn btn-primary">
            <i className="bi bi-search me-2"></i>求人を探す
          </a>
        </div>
      </div>
    );
  }

  // 応募済み案件一覧の表示
  return (
    <div className="container p-4">
      <h1 className="h3 fw-bold mb-4">応募済み案件一覧</h1>
      <div className="row">
        <div className="col-12">
          {appliedJobs.map((application) => (
            <div key={application.id} className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">
                  {application.job_listing ? (
                    <a href={`/job_listings/${application.job_listing.id}`}>
                      {application.job_listing.title}
                    </a>
                  ) : (
                    <>
                      <a href={`/job_sources/${application.job_source_id}`}>
                        {application.job_source?.display_name || application.job_source?.name}
                      </a>
                      <span className="badge bg-secondary ms-2">エージェントに相談</span>
                    </>
                  )}
                </h5>
                <p className="card-text">
                  <small className="text-muted">
                    応募日: {formatDate(application.applied_at)}
                  </small>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppliedJobListingsPage;

